import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, BookOpen } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function Glossary() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get(`/glossary?lang=${lang}`).then((r) => setItems(r.data.items || []));
  }, [lang]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (it) => it.term.toLowerCase().includes(s) || it.definition.toLowerCase().includes(s),
    );
  }, [q, items]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-[#C85A32]" />
          {t("glossary.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">{t("glossary.subtitle")}</p>
      </div>

      <div className="relative">
        <Search className="w-5 h-5 text-[#707880] absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="glossary-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("glossary.placeholder")}
          className="w-full rounded-2xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none pl-11 pr-4 py-4 text-lg bg-white"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filtered.map((it) => (
          <button
            key={it.term}
            data-testid={`glossary-item-${it.term.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setOpen(open === it.term ? null : it.term)}
            className="pod-focus text-left bg-white border-2 border-[#E4DDD3] hover:border-[#C85A32] rounded-2xl p-5 transition-colors"
          >
            <div className="text-lg font-bold text-[#1C2024]">{it.term}</div>
            <div className="text-[#4A525A] mt-1">{it.definition}</div>
            {open === it.term && (
              <div className="mt-4 space-y-3 border-t border-[#E4DDD3] pt-3">
                <Field label={t("glossary.field.example")} value={it.example} />
                <Field label={t("glossary.field.where")} value={it.where} />
                <Field label={t("glossary.field.why")} value={it.why} />
              </div>
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-[#4A525A] py-8">{t("glossary.no_match")} "{q}".</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{label}</div>
      <div className="text-[#1C2024]">{value}</div>
    </div>
  );
}
